import React from 'react';
import {
  IResourceComponentsProps,
  useNavigation,
  useNotification,
} from '@pankod/refine-core';
import { useDelete, useUpdate } from '@pankod/refine-core';
import {
  doc,
  collection,
  getDoc,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import {
  Button,
  DataGrid,
  FormControl,
  GridColumns,
  InputLabel,
  List,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useDataGrid,
} from '@pankod/refine-mui';
import { Edit, Close } from '@mui/icons-material';
import dayjs from 'dayjs';
import { OfferExpiryType, OfferPeriodType } from 'config/enum';
import { IGetOfferCodes } from 'interfaces';
import { GridActionsCellItem } from '@pankod/refine-mui';
import { writeBatch } from 'firebase/firestore';
import { fbFirestore } from 'lib/firebaseService';

type IManagerCode = IGetOfferCodes & {
  created_by_email?: string;
  created_by_role?: 'admin' | 'manager';
};

const getCodeStatus = (row: IManagerCode) => {
  if (row.device_using || (row.use || 0) > 0) {
    return 'In Use';
  }

  if (
    row.type !== OfferExpiryType.ByPeriod &&
    row.expiry_date &&
    dayjs(row.expiry_date.toDate()).isBefore(dayjs())
  ) {
    return 'Expired';
  }

  return 'Available';
};

export const ManagerOfferCodesList: React.FC<
  IResourceComponentsProps
> = () => {
  const notification = useNotification();
  const { push } = useNavigation();
  const { dataGridProps, setFilters } = useDataGrid<IManagerCode>({
    resource: 'Offers',
    initialPageSize: 10,
    initialSorter: [
      {
        field: 'created_at',
        order: 'desc',
      },
    ],
    permanentFilter: [
      {
        field: 'created_by_role',
        operator: 'eq',
        value: 'manager',
      },
    ],
  });

  const { mutateAsync: mutateDelete } = useDelete();
  const { mutate: mutateUpdate } = useUpdate();
  const [searchCode, setSearchCode] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<
    'all' | 'use' | 'expire'
  >('all');

  const handleCodeSearch = () => {
    const code = searchCode.trim();
    if (code) {
      setFilters([
        { field: 'code', operator: 'gte', value: code },
        { field: 'code', operator: 'lte', value: code + '\uf8ff' },
      ]);
    } else {
      setFilters([]);
    }
  };

  const handleStatusChange = (value: 'all' | 'use' | 'expire') => {
    setStatusFilter(value);
    if (value === 'use') {
      setFilters([{ field: 'device_using', operator: 'gt', value: '0' }]);
    } else if (value === 'expire') {
      setFilters([
        { field: 'expiry_date', operator: 'lt', value: new Date() },
      ]);
    } else {
      setFilters([]);
    }
    // clear code search when status changes
    setSearchCode('');
  };

  const resetDevice = async (row: IGetOfferCodes) => {
    if (row.device_using) {
      await mutateUpdate({
        resource: 'Offers',
        id: row.id,
        values: {
          device_using: null,
        },
      });
    } else {
      push(`/device_usings?code=${row.code}`);
    }
  };

  const deleteOffer = (row: IGetOfferCodes) => async () => {
    try {
      const offerRef = doc(collection(fbFirestore, 'Offers'), row.id);
      const offerDoc = await getDoc(offerRef);
      if (!offerDoc.exists()) {
        throw new Error('Offer not found');
      }
      const offer = offerDoc.data();

      await mutateDelete({
        resource: 'Offers',
        id: row.id,
        mutationMode: 'undoable',
      });

      const batch = writeBatch(fbFirestore);

      if (offer.use > 0) {
        const deviceUsingsQuery = query(
          collection(fbFirestore, 'device_usings'),
          where('code', '==', offer.code),
        );
        (await getDocs(deviceUsingsQuery)).forEach((doc) =>
          batch.delete(doc.ref),
        );
      }

      await batch.commit();

      notification?.open?.({
        type: 'success',
        message: 'Device deleted',
      });
    } catch (e) {
      notification?.open?.({
        type: 'error',
        message: (e as Error)?.message,
      });
    }
  };

  const columns = React.useMemo<GridColumns<IManagerCode>>(
    () => [
      {
        field: 'created_by_email',
        headerName: 'Email',
        flex: 1,
        minWidth: 100,
        disableReorder: true,
        sortable: true,
      },
      {
        field: 'code',
        headerName: 'Invite Code',
        flex: 1,
        minWidth: 100,
        disableReorder: true,
        sortable: true,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        minWidth: 80,
        disableReorder: true,
        sortable: false,
        renderCell: function render({ row }) {
          return <Typography>{getCodeStatus(row)}</Typography>;
        },
      },
      {
        field: 'Expiry date',
        headerName: 'Expiry on/after date',
        flex: 1,
        minWidth: 100,
        disableReorder: true,
        sortable: true,
        renderCell: function render({ row }) {
          const expiryDate = (() => {
            switch (row.type) {
              case OfferExpiryType.ByPeriod:
                return dayjs
                  .duration({
                    [row.period_type || OfferPeriodType.Day]:
                      row.period,
                  })
                  .humanize();
              default:
                return dayjs(row.expiry_date.toDate()).format(
                  'YYYY-MM-DD HH:mm:ss',
                );
            }
          })();
          return <Typography>{expiryDate}</Typography>;
        },
      },
      {
        field: 'device_id',
        headerName: 'Device ID',
        flex: 1,
        width: 160,
        disableReorder: true,
        sortable: true,
        renderCell: function render({ row }) {
          return <Typography>{row.device_using}</Typography>;
        },
      },
      {
        field: 'actions',
        headerName: '#',
        type: 'actions',
        width: 40,
        getActions: function render({ row }) {
          return [
            <GridActionsCellItem
              key={1}
              label="Reset Device"
              icon={<Edit color="success" />}
              onClick={() => resetDevice(row)}
              showInMenu
            />,
            <GridActionsCellItem
              key={2}
              label="Delete"
              icon={<Close color="error" />}
              onClick={deleteOffer(row)}
              showInMenu
            />,
          ];
        },
      },
    ],
    [],
  );

  return (
    <List
      title="Manager Invite Codes"
      cardProps={{ sx: { paddingX: { xs: 0, md: 0 } } }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ mb: 2 }}
        component="form"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          handleCodeSearch();
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search by code"
          value={searchCode}
          onChange={(event) => {
            setSearchCode(event.target.value);
            if (!event.target.value.trim()) {
              setFilters([]);
            }
          }}
          sx={{ minWidth: { xs: '100%', md: 240 } }}
        />
        <Button variant="contained" type="submit" size="small">
          Search
        </Button>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="manager-code-status-label">
            Status
          </InputLabel>
          <Select
            labelId="manager-code-status-label"
            value={statusFilter}
            label="Status"
            onChange={(event) =>
              handleStatusChange(
                event.target.value as 'all' | 'use' | 'expire',
              )
            }
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="use">In Use</MenuItem>
            <MenuItem value="expire">Expired</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <DataGrid
        {...dataGridProps}
        columns={columns}
        autoHeight
        disableColumnFilter={true}
        disableColumnSelector={true}
        rowsPerPageOptions={[5, 10, 15, 20]}
        density="comfortable"
      />
    </List>
  );
};
